import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './rush.css';
import './languages.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { LANGUAGES, PROVIDERS, DEEPL_LANGS, ANTHROPIC_GOOD } from './languagesData';

const FONT_BY_SCRIPT = {
  Han: "'Noto Sans SC', 'Noto Sans', sans-serif",
  'Han/Kana': "'Noto Sans JP', 'Noto Sans', sans-serif",
  Hangul: "'Noto Sans KR', 'Noto Sans', sans-serif",
  Arabic: "'Noto Sans Arabic', 'Noto Sans', sans-serif",
  Hebrew: "'Noto Sans Hebrew', 'Noto Sans', sans-serif",
  Devanagari: "'Noto Sans Devanagari', 'Noto Sans', sans-serif",
  Thai: "'Noto Sans Thai', 'Noto Sans', sans-serif",
};

function providersFor(lang) {
  const out = [];
  if (DEEPL_LANGS.has(lang.code)) out.push('deepl');
  out.push('google');
  if (lang.tier <= 2) out.push('openai');
  if (ANTHROPIC_GOOD.has(lang.code)) out.push('anthropic');
  if (lang.tier <= 2) out.push('microsoft');
  if (lang.tier === 1) out.push('amazon');
  return out;
}

const Hero = () => (
  <section className="lang-hero">
    <div className="hero-mesh"></div>
    <div className="rs-container hero-anim" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
      <span className="hero-eyebrow"><span className="dot"></span>{LANGUAGES.length} languages supported</span>
      <h1 className="lang-h1">Every language your team translates into.</h1>
      <p className="lang-lead">From Mandarin to Maltese. Production-ready AI translation with translation memory and glossaries built in — plus human review available where you need it. Searchable, filterable, and transparent about what each language pair actually does today.</p>
    </div>
  </section>
);

const TIERS = [
  { n: 1, label: 'Core', color: 'tier-1', headline: 'Production-ready AI + memory & glossaries', desc: 'Our best-supported pairs: high-confidence AI translation with translation memory, glossaries, and preserved formatting. Human review available on request.' },
  { n: 2, label: 'Supported', color: 'tier-2', headline: 'AI translation with full TM & glossary support', desc: 'Dependable pairs with translation memory and glossary support built in. Human review available on request as your volume grows.' },
  { n: 3, label: 'Early', color: 'tier-3', headline: 'AI translation, in active development', desc: "Newer pairs where AI quality is improving fast. We're adding human review as the reviewer pool grows — expect rapid gains here." },
];

const TierLegend = () => (
  <section className="section-tight" style={{ paddingTop: 24 }}>
    <div className="rs-container">
      <div className="tier-grid reveal stagger">
        {TIERS.map((t) => (
          <div className={`tier-card ${t.color}`} key={t.n}>
            <div className="tier-head">
              <span className={`tier-badge ${t.color}`}>Tier {t.n}</span>
              <span className="tier-count">{LANGUAGES.filter((l) => l.tier === t.n).length} languages</span>
            </div>
            <h3 className="tier-label">{t.label}</h3>
            <p className="tier-headline">{t.headline}</p>
            <p className="tier-desc">{t.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const REGIONS = [
  ['all', 'All regions'],
  ['europe', 'Europe'],
  ['americas', 'Americas'],
  ['asia', 'Asia-Pacific'],
  ['mena', 'MENA'],
  ['africa', 'Africa'],
  ['global', 'Global'],
];

const LanguageCard = ({ lang }) => {
  const font = FONT_BY_SCRIPT[lang.script] || "'Noto Sans', 'Inter', sans-serif";
  const provs = providersFor(lang);
  const visible = provs.slice(0, 4);
  const overflow = provs.length - visible.length;
  return (
    <div className="lang-card">
      <div className="lang-providers" aria-label="AI engines that support this language">
        {visible.map((p) => {
          const meta = PROVIDERS[p];
          return (
            <span key={p} className="prov-chip" style={{ background: meta.bg, color: meta.fg, borderColor: meta.bg === '#fff' ? 'var(--line)' : meta.bg }} title={meta.name}>
              {meta.logo}
            </span>
          );
        })}
        {overflow > 0 && <span className="prov-chip prov-chip-more" title={provs.slice(4).map((p) => PROVIDERS[p].name).join(', ')}>+{overflow}</span>}
      </div>
      <div className="lang-card-top">
        <span className={`lang-tier-dot tier-${lang.tier}`} title={`Tier ${lang.tier}`}></span>
        <span className="lang-code">{lang.code}</span>
        {lang.rtl && <span className="lang-flag rtl-flag" title="Right-to-left">RTL</span>}
      </div>
      <div className="lang-native" style={{ fontFamily: font }} dir={lang.rtl ? 'rtl' : 'ltr'}>{lang.native}</div>
      <div className="lang-name">{lang.name}</div>
      <div className="lang-meta">
        <span className="lang-script">{lang.script}</span>
      </div>
    </div>
  );
};

const Explorer = () => {
  const [query, setQuery] = useState('');
  const [region, setRegion] = useState('all');
  const [tier, setTier] = useState(0);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return LANGUAGES.filter((l) => {
      if (region !== 'all' && l.region !== region) return false;
      if (tier !== 0 && l.tier !== tier) return false;
      if (!q) return true;
      return (
        l.name.toLowerCase().includes(q) ||
        l.native.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q) ||
        l.script.toLowerCase().includes(q)
      );
    });
  }, [query, region, tier]);

  return (
    <section className="section" id="explore">
      <div className="rs-container">
        <div className="section-head reveal">
          <div className="eyebrow">Explore</div>
          <h2>Find your language.</h2>
          <p className="section-lead">Search by name, native script, or ISO code. Filter by region or quality tier. {LANGUAGES.length} options — and growing.</p>
        </div>

        <div className="prov-legend reveal">
          <span className="prov-legend-label">AI engines:</span>
          {Object.entries(PROVIDERS).map(([key, meta]) => (
            <span key={key} className="prov-legend-item">
              <span className="prov-chip" style={{ background: meta.bg, color: meta.fg, borderColor: meta.bg === '#fff' ? 'var(--line)' : meta.bg }}>{meta.logo}</span>
              <span>{meta.name}</span>
            </span>
          ))}
        </div>

        <div className="lang-controls reveal">
          <div className="lang-search">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.8" />
              <path d="M12 12l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input type="search" className="input lang-search-input" placeholder={'Try "japanese", "العربية", or "de"…'} value={query} onChange={(e) => setQuery(e.target.value)} autoComplete="off" />
            {query && (
              <button className="lang-search-clear" onClick={() => setQuery('')} aria-label="Clear search">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
              </button>
            )}
          </div>

          <div className="lang-filter-row">
            <div className="lang-filter-group">
              <span className="lang-filter-label">Region</span>
              <div className="lang-filter-chips">
                {REGIONS.map(([id, label]) => (
                  <button key={id} className={`filter-chip ${region === id ? 'active' : ''}`} onClick={() => setRegion(id)}>{label}</button>
                ))}
              </div>
            </div>
            <div className="lang-filter-group">
              <span className="lang-filter-label">Tier</span>
              <div className="lang-filter-chips">
                <button className={`filter-chip ${tier === 0 ? 'active' : ''}`} onClick={() => setTier(0)}>All</button>
                <button className={`filter-chip ${tier === 1 ? 'active' : ''}`} onClick={() => setTier(1)}><span className="tier-dot tier-1"></span>Core</button>
                <button className={`filter-chip ${tier === 2 ? 'active' : ''}`} onClick={() => setTier(2)}><span className="tier-dot tier-2"></span>Supported</button>
                <button className={`filter-chip ${tier === 3 ? 'active' : ''}`} onClick={() => setTier(3)}><span className="tier-dot tier-3"></span>Early</button>
              </div>
            </div>
          </div>

          <div className="lang-result-count">
            <span><b>{filtered.length}</b> {filtered.length === 1 ? 'language' : 'languages'} {(query || region !== 'all' || tier !== 0) && 'matching'}</span>
            {(query || region !== 'all' || tier !== 0) && (
              <button className="link-btn" onClick={() => { setQuery(''); setRegion('all'); setTier(0); }}>Clear filters</button>
            )}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="lang-empty reveal">
            <div className="lang-empty-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none"><circle cx="14" cy="14" r="9" stroke="currentColor" strokeWidth="2" /><path d="M21 21l6 6M11 11l6 6M17 11l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
            </div>
            <h4>No matches.</h4>
            <p>We don't currently support a language matching those filters. <Link to="/contact">Tell us</Link> what you need — we add 3-5 new languages per quarter.</p>
          </div>
        ) : (
          <div className="lang-grid reveal">
            {filtered.map((l) => <LanguageCard key={l.code} lang={l} />)}
          </div>
        )}
      </div>
    </section>
  );
};

const POPULAR_PAIRS = [
  { src: 'EN', tgt: 'DE', srcName: 'English', tgtName: 'German', tmSize: '4.2M segments', quality: 98 },
  { src: 'EN', tgt: 'FR', srcName: 'English', tgtName: 'French', tmSize: '3.8M segments', quality: 98 },
  { src: 'EN', tgt: 'ES', srcName: 'English', tgtName: 'Spanish', tmSize: '3.5M segments', quality: 97 },
  { src: 'EN', tgt: 'JA', srcName: 'English', tgtName: 'Japanese', tmSize: '2.1M segments', quality: 95 },
  { src: 'EN', tgt: 'ZH', srcName: 'English', tgtName: 'Chinese (Simp.)', tmSize: '2.8M segments', quality: 96 },
  { src: 'EN', tgt: 'PT-BR', srcName: 'English', tgtName: 'Portuguese (BR)', tmSize: '1.9M segments', quality: 97 },
];

const PopularPairs = () => (
  <section className="section" style={{ background: 'var(--bg-soft)', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
    <div className="rs-container">
      <div className="section-head reveal" style={{ textAlign: 'left', margin: '0 0 40px', maxWidth: 'none' }}>
        <div className="eyebrow">Popular pairs</div>
        <h2 style={{ fontSize: 36 }}>The translations our customers ship most.</h2>
        <p className="section-lead">Public translation memory available for every pair below — yours grows on top of it.</p>
      </div>
      <div className="pair-grid reveal stagger">
        {POPULAR_PAIRS.map((p) => (
          <div className="pair-card" key={p.src + p.tgt}>
            <div className="pair-codes">
              <span className="pair-code src">{p.src}</span>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="pair-arrow"><path d="M4 10h12m0 0l-4-4m4 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
              <span className="pair-code tgt">{p.tgt}</span>
            </div>
            <div className="pair-names">
              <span>{p.srcName}</span>
              <span className="pair-arrow-text">→</span>
              <span>{p.tgtName}</span>
            </div>
            <div className="pair-stats">
              <div><div className="pair-stat-num">{p.tmSize}</div><div className="pair-stat-label">Public TM</div></div>
              <div><div className="pair-stat-num">{p.quality}<span style={{ fontSize: 18 }}>%</span></div><div className="pair-stat-label">Review pass rate</div></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const CAPABILITIES = [
  { title: 'Right-to-left support', desc: 'Arabic, Hebrew, Persian, Urdu, Pashto. Bidirectional layout preserved through DOCX, IDML, HTML export.', icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M16 5H8a4 4 0 000 8h2m6-8l-3-3m3 3l-3 3M6 17h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg> },
  { title: 'CJK & complex scripts', desc: 'Chinese, Japanese, Korean, Thai, Khmer, Burmese — vertical text, ruby annotations, line-breaking handled.', icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="3" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M11 6v10M6 11h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg> },
  { title: 'Regional variants', desc: 'Spanish (Spain) vs Spanish (LatAm). French (France) vs French (Canada). Portuguese (PT) vs (BR). All distinct memories.', icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.8" /><path d="M3 11h16M11 3a12 12 0 010 16M11 3a12 12 0 000 16" stroke="currentColor" strokeWidth="1.5" /></svg> },
  { title: 'Locale-aware formatting', desc: 'Dates, currencies, numbers, units automatically reformat per locale. ICU MessageFormat. Plurals + gender.', icon: <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><rect x="3" y="4" width="16" height="14" rx="2" stroke="currentColor" strokeWidth="1.8" /><path d="M3 8h16M7 12h2M11 12h2M15 12h2M7 15h2M11 15h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg> },
];

const Capabilities = () => (
  <section className="section">
    <div className="rs-container">
      <div className="section-head reveal">
        <div className="eyebrow">Built for real languages</div>
        <h2>Not just "supports Unicode."</h2>
        <p className="section-lead">Translation tools love to claim 100+ languages. Then your RTL layout breaks, your CJK line-heights collapse, and your "Spanish" means Spain only. Rush Studio actually handles the hard parts.</p>
      </div>
      <div className="cap-grid reveal stagger">
        {CAPABILITIES.map((c) => (
          <div className="cap-card" key={c.title}>
            <div className="cap-icon">{c.icon}</div>
            <div className="cap-title">{c.title}</div>
            <div className="cap-desc">{c.desc}</div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const MissingCTA = () => (
  <section className="section">
    <div className="rs-container">
      <div className="missing-card reveal">
        <div className="missing-copy">
          <div className="eyebrow">Don't see your language?</div>
          <h2 className="missing-h">Tell us — we add 3-5 per quarter.</h2>
          <p>We prioritize new language pairs based on customer demand. If yours isn't here yet, write to us and we'll let you know when it lands.</p>
          <div className="missing-cta-row">
            <Link className="btn btn-accent" to="/contact">Request a language →</Link>
            <a className="btn btn-ghost" href="#">See our roadmap</a>
          </div>
        </div>
        <div className="missing-visual">
          <div className="missing-stack">
            {['Esperanto', 'Sundanese', 'Wolof', 'Quechua', 'Aymara', 'Cherokee'].map((n, i) => (
              <span key={n} className="missing-chip" style={{ animationDelay: `${i * 100}ms` }}>{n}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const RushLanguagesPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="languages" />
      <Hero />
      <TierLegend />
      <Explorer />
      <PopularPairs />
      <Capabilities />
      <MissingCTA />
      <RushFooter />
    </div>
  );
};

export default RushLanguagesPage;
